import { useCallback, useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { ACCOUNT_TYPES, getAccountType, ACCOUNT_TYPE_TONE } from '@/lib/accountTypes';

// Compact pill that displays the current account type for a position
// and (when `clickable`) opens the `AccountTypeModal` on click.

export function AccountTypeBadge({ value, onClick, testId, size = 'sm' }) {
  const at = getAccountType(value);
  const Icon = at.icon;
  const tone = ACCOUNT_TYPE_TONE[at.key] || ACCOUNT_TYPE_TONE.other;
  const cls = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-[11px] px-2 py-0.5';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={onClick ? `Account: ${at.label} (click to change)` : at.label}
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full ring-1 font-semibold whitespace-nowrap ${tone} ${cls} ${onClick ? 'hover:brightness-110 cursor-pointer' : 'cursor-default'}`}
    >
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span>{at.short}</span>
    </button>
  );
}

// Modal that lets the user pick a new account type from the 6-value
// enum. Closing the modal (Esc / outside click / × button) persists
// whatever radio is currently selected via onSave. When `ticker` is
// passed, a scope selector renders at the top so the user can choose
// "Change this Lot only" (default) or "Change all Lots for {ticker}" —
// big time-saver for users whose entire position lives in one account.

export default function AccountTypeModal({ open, currentValue, onSave, onClose, title = 'Set Account Type', ticker }) {
  const wrapRef = useRef(null);
  const confirmRef = useRef(null);
  const selectedRef = useRef(currentValue || 'taxable');
  const scopeRef = useRef('lot'); // 'lot' | 'all' | 'portfolio'
  const [confirmPortfolio, setConfirmPortfolio] = useState(false);

  // Reset selection whenever modal opens
  useEffect(() => {
    if (open) {
      selectedRef.current = currentValue || 'taxable';
      scopeRef.current = 'lot';
      setConfirmPortfolio(false);
    }
  }, [open, currentValue]);

  // Round P108v (Feb 2026) — auto-scroll the modal to reveal the
  // Yes/Cancel confirmation panel the moment it appears. Previously
  // it rendered below the long account-type list, out of the
  // viewport, and users didn't know it was there.
  useEffect(() => {
    if (confirmPortfolio && confirmRef.current) {
      confirmRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [confirmPortfolio]);

  const handleClose = useCallback(() => {
    const chosen = selectedRef.current;
    const scope = scopeRef.current;
    // For scope=portfolio we require a confirmation step before
    // pushing the change (so a stray X-click can't nuke every tag).
    // If the user hasn't confirmed yet, just stop closing; the
    // confirmation dialog will appear.
    if (scope === 'portfolio' && !confirmPortfolio) {
      setConfirmPortfolio(true);
      return;
    }
    if (chosen && (scope === 'all' || scope === 'portfolio' || chosen !== currentValue)) {
      onSave?.(chosen, { scope });
    }
    onClose?.();
  }, [onSave, onClose, currentValue, confirmPortfolio]);

  // Outside click + Esc
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) handleClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="account-type-modal">
      <div
        ref={wrapRef}
        // Phone fix (Feb 2026): mirror the BrokerModal — bump bottom
        // padding on mobile so the "apply to all / portfolio" action
        // buttons clear the floating Emergent badge in the lower-right
        // of phone viewports.
        className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-4 pb-24 sm:pb-4 max-h-[90vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold">{title}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground"
            data-testid="account-type-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
          Pick the account this position lives in. We use this to figure out
          which dividends face the 3.8% NIIT surtax on your Year-End Report.
          The selected option saves when you close this modal.
        </p>
        {ticker && (
          <fieldset
            className="mb-3 pb-3 border-b border-border space-y-1.5"
            data-testid="account-type-scope"
          >
            <legend className="sr-only">Apply to</legend>
            <ScopeRow
              value="lot"
              label="Change this Lot only"
              hint={`Update just this single purchase of ${ticker}.`}
              defaultChecked
              onSelect={(v) => { scopeRef.current = v; }}
            />
            <ScopeRow
              value="all"
              label={`Change ALL ${ticker} lots`}
              hint={`Tag every lot of ${ticker} in your portfolio at once. Handy if you keep the whole ticker in one account (e.g. 100% Roth IRA).`}
              onSelect={(v) => { scopeRef.current = v; }}
            />
            <ScopeRow
              value="portfolio"
              label="Change EVERY lot in my portfolio"
              hint="One-click bulk tag for users whose entire portfolio lives in a single account (Roth, Trad IRA, 401k, etc.). Confirmation required."
              danger
              onSelect={(v) => { scopeRef.current = v; }}
            />
          </fieldset>
        )}
        <fieldset className="space-y-1.5">
          <legend className="sr-only">Account type</legend>
          {ACCOUNT_TYPES.map((at) => (
            <AccountTypeRow
              key={at.key}
              at={at}
              initialChecked={at.key === (currentValue || 'taxable')}
              onSelect={(k) => { selectedRef.current = k; }}
            />
          ))}
        </fieldset>
        {confirmPortfolio && (
          <div ref={confirmRef} className="mt-3 pt-3 border-t-2 border-rose-300 dark:border-rose-700" data-testid="account-type-portfolio-confirm">
            <div className="flex items-start gap-2 p-3 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-300 dark:border-rose-700">
              <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-300 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-rose-900 dark:text-rose-200 mb-1">
                  Tag every lot in your entire portfolio?
                </div>
                <p className="text-[11px] text-rose-800 dark:text-rose-300/90 leading-snug mb-2">
                  This will set <strong>every position across every ticker</strong>{' '}
                  to the account type you selected above. There&rsquo;s no undo
                  beyond manually re-tagging lots. Use this only if you keep
                  your whole portfolio in a single account (e.g., 100% Roth IRA).
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      // Confirmed — emit save and close
                      onSave?.(selectedRef.current, { scope: 'portfolio' });
                      onClose?.();
                    }}
                    className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold"
                    data-testid="account-type-portfolio-confirm-yes"
                  >
                    Yes, tag everything
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // User backed out — revert to single-lot scope
                      // and clear the confirm flag so X / Esc work
                      // normally again.
                      scopeRef.current = 'lot';
                      setConfirmPortfolio(false);
                    }}
                    className="px-3 py-1.5 rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 text-xs font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/40"
                    data-testid="account-type-portfolio-confirm-no"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccountTypeRow({ at, initialChecked, onSelect }) {
  const Icon = at.icon;
  return (
    <label
      className="flex items-start gap-2 p-2 rounded-md border border-border hover:border-primary/50 hover:bg-muted/40 cursor-pointer"
      data-testid={`account-type-option-${at.key}`}
    >
      <input
        type="radio"
        name="account-type"
        value={at.key}
        defaultChecked={initialChecked}
        onChange={() => onSelect(at.key)}
        className="mt-1 accent-primary"
        data-testid={`account-type-radio-${at.key}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
          <span>{at.label}</span>
          {!at.niit_applies && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 ml-1">
              NIIT-exempt
            </span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">
          {at.hint}
        </p>
      </div>
    </label>
  );
}

function ScopeRow({ value, label, hint, defaultChecked, onSelect, danger }) {
  const ringCls = danger
    ? 'hover:bg-rose-50 dark:hover:bg-rose-950/30'
    : 'hover:bg-muted/40';
  const labelCls = danger
    ? 'text-rose-700 dark:text-rose-300'
    : '';
  return (
    <label
      className={`flex items-start gap-2 p-1.5 rounded-md cursor-pointer ${ringCls}`}
      data-testid={`account-type-scope-${value}`}
    >
      <input
        type="radio"
        name="account-type-scope"
        value={value}
        defaultChecked={defaultChecked}
        onChange={() => onSelect(value)}
        className="mt-1 accent-primary"
        data-testid={`account-type-scope-radio-${value}`}
      />
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] font-semibold leading-tight ${labelCls}`}>{label}</div>
        <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{hint}</p>
      </div>
    </label>
  );
}
